/**
 * Animated disclosure rows.
 *
 * A native <details> snaps open, which on a panel of product information reads
 * as the page jumping rather than as content arriving. This animates the
 * height on the way in and on the way out.
 *
 * The element stays a real <details>: closed content remains in the document,
 * so it is still found by in-page search and still indexed. Without this
 * module the rows open and close exactly as the browser intends.
 *
 * Applies to any <details data-disclosure> whose first child is the summary.
 */
import { animate, prefersReducedMotion } from 'theme:motion';

const DURATION = 0.34;
const EASING = [0.16, 1, 0.3, 1];

document.querySelectorAll('details[data-disclosure]').forEach((details) => {
  const summary = details.querySelector('summary');
  const panel = summary?.nextElementSibling;
  if (!summary || !panel) return;

  let running = null;

  const measure = () => `${panel.scrollHeight}px`;

  summary.addEventListener('click', (event) => {
    if (prefersReducedMotion()) return;

    event.preventDefault();
    running?.cancel();

    const opening = !details.open;

    if (opening) {
      // The element has to be open before the panel can be measured.
      details.open = true;
      running = animate(
        panel,
        { height: ['0px', measure()], opacity: [0, 1] },
        { duration: DURATION, easing: EASING }
      );
      running.finished
        .then(() => {
          panel.style.height = '';
          panel.style.opacity = '';
        })
        .catch(() => {});
    } else {
      running = animate(
        panel,
        { height: [measure(), '0px'], opacity: [1, 0] },
        { duration: DURATION, easing: EASING }
      );
      running.finished
        .then(() => {
          details.open = false;
          panel.style.height = '';
          panel.style.opacity = '';
        })
        .catch(() => {});
    }
  });

  // Keeps the caret in sync when a row is opened by find-in-page rather than
  // by a click, which does not fire the handler above.
  details.addEventListener('toggle', () => {
    summary.setAttribute('aria-expanded', String(details.open));
  });

  summary.setAttribute('aria-expanded', String(details.open));
});
